import React, { useEffect, useState } from 'react';
import {
  Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, IconButton, Button, Alert, Switch, Tooltip,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import HelpTip, { HelpContent } from '../../common/HelpTip';
import { TableCard, tableHeadCellSx } from '../tabLayout';
import SearchableSelect from '../../common/SearchableSelect';
import { ToneChip } from '../../common/StatusChip';
import { MoveButtons, QuietNote, SchoolClass, numSx, useClassNames } from './sectionParts';
import { formatMoneyFull } from '../../reports/finance/chartConfig';
import { useAuth } from '../../../context/AuthContext';
import { AdmissionCycle, AdmissionFeeHead } from '../../../types/admissions';

const HELP: Record<'refundable' | 'optional', HelpContent> = {
  refundable: {
    title: 'Refundable',
    text: 'Paid back, in part or in full, if the student withdraws — by the refund policy below.',
  },
  optional: {
    title: 'Optional',
    text: 'Left off the challan unless the parent picks it when accepting the offer — e.g. transport or uniform.',
  },
};

interface Props {
  cycle: AdmissionCycle;
  classes: SchoolClass[];
  loadingClasses: boolean;
  canEdit: boolean;
  /** Classes whose fees are fixed on this open cycle — families hold offers at them. */
  lockedClassIds?: string[];
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * Admission-fee breakdown. One row per fee head — admission fee, security
 * deposit, annual charges, transport, uniform, etc. Order is preserved on
 * the challan PDF and the parent-facing status link. Flags:
 *   • refundable — whether the slab policy applies on withdrawal
 *   • optional   — whether the parent can opt out at offer acceptance
 *   • classId    — null = applies to all classes
 */

const AdmissionFeesSection: React.FC<Props> = ({ cycle, classes, loadingClasses, canEdit, lockedClassIds = [], onSave }) => {
  const { currency } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<AdmissionFeeHead[]>([]);
  const { nameOf, options: classOptions } = useClassNames(classes);

  useEffect(() => {
    if (editing) setDraft(cycle.admissionFeeHeads ?? []);
  }, [editing, cycle.admissionFeeHeads]);

  const addRow = () => {
    setDraft([
      ...draft,
      { classId: null, label: '', amount: 0, refundable: false, optional: false },
    ]);
  };

  const update = (idx: number, patch: Partial<AdmissionFeeHead>) => {
    setDraft(draft.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const remove = (idx: number) => setDraft(draft.filter((_, i) => i !== idx));

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...draft];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setDraft(next);
  };

  const hasInvalid = draft.some(r => !r.label.trim() || r.amount < 0);
  const total = (editing ? draft : (cycle.admissionFeeHeads ?? []))
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ admissionFeeHeads: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = editing ? draft : (cycle.admissionFeeHeads ?? []);
  const hasOptional = rows.some(r => r.optional);

  return (
    <SectionCard
      title="Admission fee breakdown"
      description="Line items on the admission-fee challan, printed in this order."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft(cycle.admissionFeeHeads ?? [])}
      saving={saving}
      canSave={!hasInvalid}
      headerActions={
        <Button size="small" startIcon={<Add fontSize="small" />} onClick={addRow}>
          Add fee head
        </Button>
      }
    >
      {lockedClassIds.length > 0 && (
        <QuietNote sx={{ mb: 2 }}>
          Families holding offers in {lockedClassIds.map(id => nameOf(id)).join(', ')} were quoted these fees, so their fees and fees for every class are fixed. Fees for other classes can still change.
        </QuietNote>
      )}
      {rows.length === 0 ? (
        <QuietNote>No admission fees — confirmed offers won't raise a fee challan.</QuietNote>
      ) : (
        <>
          <TableCard
            footer={hasOptional && !editing ? 'Optional items are left off by default — parents opt in when they accept the offer.' : undefined}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Fee</TableCell>
                  <TableCell sx={tableHeadCellSx}>Applies to</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">Amount</TableCell>
                  <TableCell sx={tableHeadCellSx} align="center"><HelpTip label="Refundable" {...HELP.refundable} /></TableCell>
                  <TableCell sx={tableHeadCellSx} align="center"><HelpTip label="Optional" {...HELP.optional} /></TableCell>
                  {editing && <TableCell sx={tableHeadCellSx} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={`fh-${idx}`}>
                    <TableCell>
                      {editing ? (
                        <TextField
                          size="small"
                          value={row.label}
                          onChange={(e) => update(idx, { label: e.target.value })}
                          placeholder="Admission fee / Security deposit / …"
                          fullWidth
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={500}>{row.label}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <SearchableSelect
                          value={row.classId ?? ''}
                          onChange={(v) => update(idx, { classId: v || null })}
                          options={classOptions}
                          placeholder="All classes"
                          loading={loadingClasses}
                          minWidth={180}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {row.classId ? nameOf(row.classId) : 'All classes'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editing ? (
                        <TextField
                          type="number"
                          size="small"
                          value={row.amount}
                          onChange={(e) => update(idx, { amount: Math.max(0, parseFloat(e.target.value || '0') || 0) })}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                          sx={{ width: 120 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ ...numSx, whiteSpace: 'nowrap' }}>
                          {formatMoneyFull(row.amount, currency)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {editing ? (
                        <Switch
                          size="small"
                          checked={!!row.refundable}
                          onChange={(e) => update(idx, { refundable: e.target.checked })}
                        />
                      ) : (
                        <YesNo value={!!row.refundable} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {editing ? (
                        <Switch
                          size="small"
                          checked={!!row.optional}
                          onChange={(e) => update(idx, { optional: e.target.checked })}
                        />
                      ) : (
                        <YesNo value={!!row.optional} />
                      )}
                    </TableCell>
                    {editing && (
                      <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end">
                          <MoveButtons index={idx} count={rows.length} onMove={(dir) => move(idx, dir)} />
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => remove(idx)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { borderBottom: 0 } }}>
                  <TableCell colSpan={2}>
                    <Typography variant="body2" fontWeight={600}>Total</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} sx={{ ...numSx, whiteSpace: 'nowrap' }}>
                      {formatMoneyFull(total, currency)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={editing ? 3 : 2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableCard>
          {editing && hasInvalid && (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                Each row needs a label and a non-negative amount.
              </Typography>
            </Alert>
          )}
        </>
      )}
    </SectionCard>
  );
};

const YesNo: React.FC<{ value: boolean }> = ({ value }) => (
  value
    ? <ToneChip label="Yes" tone="neutral" />
    : <Typography variant="body2" color="text.disabled">No</Typography>
);

export default AdmissionFeesSection;
